import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH, MOBILE_WIDTH } from "helpers/ScreenSizes";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import GiftCardsTable from "../tables/GiftCardsTable";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function GiftCards() {
  const location = useLocation();
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.tickets.001")));
      return;
    }


    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.tickets.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.tickets.003"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  return (
    <Page
      title={t("Gift Cards Report")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.tickets.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.tickets.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.tickets.007"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.reports.pages.tickets.008"), url: "/admin/report" }]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container items-center">
            <DatePickerInput
              label={t("Issued On")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
          </div>
        </Card.Section>
      </Card>
      <div className="dynamic-table-container">
        <GiftCardsTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
